import { useVehicle } from 'contexts'

import React from 'react'
import { Customizations } from 'models'
import { vehicleSheetLabels } from 'utils/labels'
import VehicleSheetDocuments from '../VehicleSheetDocuments/VehicleSheetDocuments'
import VehicleSheetEquipments from '../VehicleSheetEquipments/VehicleSheetEquipments'

type VehicleSheetLinesGroupProps = { arrayOfKeys: (keyof Customizations)[]; className?: string }

const monetaryFields = [
  'providedFRE',
  'reserveTTCHF',
  'estimationApporteur',
  'coteArgusKm',
  'coteArgusPriceDate',
  'coteMarche',
] as const
type MonetaryField = (typeof monetaryFields)[number]

const noteFields = ['noteExt', 'noteInt', 'noteCT', 'noteBilanExpert'] as const
type NoteField = (typeof noteFields)[number]

const priceFormatter = new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0,
})

const formatVehicleSheetValue = (key: keyof Customizations, value: unknown): React.ReactNode => {
  if (noteFields.includes(key as NoteField)) {
    return typeof value === 'number' && Number.isFinite(value) ? value : '-'
  }

  if (monetaryFields.includes(key as MonetaryField)) {
    if (typeof value === 'number') return priceFormatter.format(value)

    if (typeof value === 'string' && value.trim() !== '') {
      const amount = Number(value)
      return Number.isFinite(amount) ? priceFormatter.format(amount) : '—'
    }

    return '—'
  }

  return value as string
}

const VehicleSheetLinesGroup = ({ arrayOfKeys, className }: VehicleSheetLinesGroupProps) => {
  const currentVehicle = useVehicle()

  type Display = {
    [key in keyof Partial<Customizations>]: React.ReactNode
  }

  const display: Display = {
    equipments: <VehicleSheetEquipments key="equip" />,
    documents: <VehicleSheetDocuments key="docs" />,
  }

  return (
    <div className={className}>
      {arrayOfKeys.map((key) => {
        return display[key] ? (
          display[key]
        ) : (
          <p
            key={key}
            className="vehicle-sheet__field"
          >
            <span className="vehicle-sheet__field-label">{vehicleSheetLabels[key]?.label}</span>
            {formatVehicleSheetValue(key, currentVehicle[key as keyof typeof currentVehicle])}
          </p>
        )
      })}
    </div>
  )
}

export default VehicleSheetLinesGroup
